import {
  enviarFacturaElectronica,
  consultarFacturaElectronica,
} from "../facturaElectronica.dtes";
import {
  FacturaElectronicaRequest,
  FacturaElectronicaResponse,
  CuerpoDocumento,
  Receptor,
  Resumen,
  Pago,
  Extension,
} from "../types/dtes.types";
import { Amortization } from "../../amortization.api";
import { Paciente } from "../../paciente.api";
import { generateDUIFromCode } from "../../../pages/FacturaElectronica/utils/duiFormatter";
import { numeroALetras } from "../../../utils/numeroALetras";
import { calcularIVADesdeBase } from "../../../utils/ivaCalculator";
import { getDepartamentos, getMunicipios } from "../../Catalogos/catalogos.api";
import { resolveLocationCodes } from "../../../hooks/useLocationResolver";

// Interface para datos de facturación automática
export interface AutoFacturacionData {
  amortization: Amortization;
  paciente: Paciente;
  transactionId?: string;
  authorizationCode?: string;
  paymentDate?: string;
  provider?: string;
}

// Interface para preview de factura
export interface FacturaPreview {
  factura: FacturaElectronicaRequest;
  resumen: {
    cliente: string;
    monto: number;
    iva: number;
    total: number;
    descripcion: string;
  };
}

export class AutoFacturacionService {
  private static readonly SONRIE_NIT =
    import.meta.env.VITE_SONRIE_NIT || "06141803690096";
  private static readonly DEBUG_MODE =
    import.meta.env.VITE_FACTURA_DEBUG === "true";

  /**
   * Genera una factura electrónica automáticamente después de un pago exitoso
   */
  public static async generarFacturaAutomatica(
    data: AutoFacturacionData,
    preview: boolean = this.DEBUG_MODE
  ): Promise<FacturaElectronicaResponse | FacturaPreview> {
    try {
      // Crear la factura
      const factura = await this.crearFacturaDesdeAmortizacion(data);

      if (preview) {
        return this.crearFacturaPreview(factura, data);
      }

      const response = await enviarFacturaElectronica(factura);

      return response;
    } catch (error) {
      console.error("Error generando factura electrónica automática:", error);
      throw error;
    }
  }

  /**
   * Crea la estructura de factura a partir de datos de amortización
   */
  private static async crearFacturaDesdeAmortizacion(
    data: AutoFacturacionData
  ): Promise<FacturaElectronicaRequest> {
    const { amortization, paciente, transactionId, provider } = data;

    // Crear receptor (paciente)
    const receptor = await this.crearReceptor(paciente);

    // Crear cuerpo del documento
    const cuerpoDocumentoOriginal = this.crearCuerpoDocumento(amortization);

    // TRANSFORMACIÓN: Aplicar transformación para que precioUni incluya IVA en el API
    const cuerpoDocumento = this.transformarCuerpoDocumentoParaAPI(
      cuerpoDocumentoOriginal
    );

    // Crear resumen con cálculos
    const resumen = this.crearResumen(amortization, transactionId, provider);

    // Crear extensión
    const extension = this.crearExtension(
      amortization,
      paciente,
      transactionId,
      provider
    );

    const factura: FacturaElectronicaRequest = {
      nit: this.SONRIE_NIT,
      receptor,
      cuerpoDocumento,
      documentoRelacionado: null,
      otrosDocumentos: null,
      ventaTercero: null,
      resumen,
      extension,
      apendice: null, // NULL como en el manual exitoso
    };

    return factura;
  }

  /**
   * Crea el receptor (paciente) para la factura
   * Ahora resuelve correctamente los códigos de departamento y municipio desde IDs
   */
  private static async crearReceptor(paciente: Paciente): Promise<Receptor> {
    // Construir nombre completo
    const nombreCompleto = [
      paciente.nombre1,
      paciente.nombre2,
      paciente.apellido1,
      paciente.apellido2,
    ]
      .filter(Boolean)
      .join(" ");

    // Obtener documento del paciente
    const documentoPaciente = paciente.dui || paciente.codigoPaciente || "";

    // Resolver códigos de departamento y municipio desde IDs
    let departamentoCodigo = paciente.codigoArea || "06"; // Fallback a San Salvador
    let municipioCodigo = "14"; // Fallback a San Salvador

    try {
      // Cargar catálogos para resolver IDs a códigos
      const [departamentos, municipios] = await Promise.all([
        getDepartamentos(),
        getMunicipios(),
      ]);

      // Resolver códigos desde IDs del paciente
      const { departamentoCodigo: deptCode, municipioCodigo: munCode } =
        resolveLocationCodes(
          paciente.departamento,
          paciente.municipio,
          departamentos,
          municipios
        );

      departamentoCodigo = deptCode;
      municipioCodigo = munCode;
    } catch (error) {
      console.warn(
        "Error al resolver ubicaciones, usando valores por defecto:",
        error
      );
      // Mantener valores por defecto ya asignados
    }

    if (!documentoPaciente) {
      // Si no hay documento, generar uno basado en el código del paciente o usar uno genérico
      const documentoGenerado = paciente.codigoPaciente
        ? generateDUIFromCode(paciente.codigoPaciente)
        : "00000000-0";

      return {
        nrc: null,
        nombre: nombreCompleto,
        codActividad: "86909", // Código de actividad como en el manual exitoso
        descActividad: "Otras actividades de atención de la salud humana", // Descripción como en el manual exitoso
        direccion: {
          departamento: departamentoCodigo,
          municipio: municipioCodigo,
          complemento: paciente.direccion || "San Salvador, El Salvador",
        },
        telefono: paciente.celular || paciente.telefono || null,
        correo: paciente.email || null,
        tipoDocumento: "13", // DUI por defecto
        numDocumento: documentoGenerado,
      };
    }

    // Validación simple del tipo de documento
    let tipoDocumento: "13" | "36" | "37" | "38" | "39" = "13"; // DUI por defecto
    let numeroFormateado = documentoPaciente;

    // Detectar tipo de documento basado en el formato
    if (documentoPaciente.includes("-") && documentoPaciente.length === 10) {
      // Formato DUI: ########-#
      tipoDocumento = "13";
      numeroFormateado = documentoPaciente;
    } else if (
      documentoPaciente.length >= 14 &&
      documentoPaciente.includes("-")
    ) {
      // Formato NIT: ##############-#
      tipoDocumento = "36";
      numeroFormateado = documentoPaciente;
    } else {
      // Otros documentos
      tipoDocumento = "37";
      numeroFormateado = documentoPaciente;
    }

    return {
      nrc: null,
      nombre: nombreCompleto,
      codActividad: "86909", // Código de actividad como en el manual exitoso
      descActividad: "Otras actividades de atención de la salud humana", // Descripción como en el manual exitoso
      direccion: {
        departamento: departamentoCodigo,
        municipio: municipioCodigo,
        complemento: paciente.direccion || "San Salvador, El Salvador",
      },
      telefono: paciente.celular || paciente.telefono || null,
      correo: paciente.email || null,
      tipoDocumento: tipoDocumento,
      numDocumento: numeroFormateado,
    };
  }

  /**
   * Crea el cuerpo del documento con los servicios/productos
   */
  private static crearCuerpoDocumento(
    amortization: Amortization
  ): CuerpoDocumento[] {
    const baseImponible = amortization.amount; // Ejemplo: $8.46

    // Usar la función correcta para calcular desde base imponible
    const calculoIVA = calcularIVADesdeBase(baseImponible);

    return [
      {
        numItem: 1,
        cantidad: 1,
        codigo: `SRV-${amortization.amortizationId}`,
        uniMedida: 59,
        descripcion: `Servicio dental - Amortización #${amortization.amortizationId}`,
        precioUni: calculoIVA.baseImponible,
        montoDescu: 0,
        ventaGravada: calculoIVA.totalConIVA,
        noGravado: 0,
        tipoItem: 1,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ivaItem: calculoIVA.iva,
        numeroDocumento: null,
        codTributo: null, // NULL como en el manual exitoso
        tributos: null,
        psv: calculoIVA.totalConIVA,
      },
    ];
  }

  /**
   * Transforma el cuerpo de documento para incluir IVA en precioUni antes de enviar al API
   */
  private static transformarCuerpoDocumentoParaAPI(
    cuerpoDocumento: CuerpoDocumento[]
  ): CuerpoDocumento[] {
    return cuerpoDocumento.map((item) => ({
      ...item,
      // TRANSFORMACIÓN: precioUni debe incluir IVA para el API
      precioUni: item.psv, // PSV ya contiene el precio con IVA incluido
    }));
  }

  /**
   * Crea el resumen con totales y formas de pago
   */
  private static crearResumen(
    amortization: Amortization,
    transactionId?: string,
    provider?: string
  ): Resumen {
    const baseImponible = amortization.amount;

    // Usar la función correcta para calcular desde base imponible
    const calculoIVA = calcularIVADesdeBase(baseImponible);

    // Determinar forma de pago según el proveedor
    let codigoPago = "01"; // Billete y moneda de curso legal
    if (provider === "wompi" || provider === "n1co") {
      codigoPago = "03"; // Tarjeta de crédito
    }

    const pagos: Pago[] = [
      {
        codigo: codigoPago,
        montoPago: calculoIVA.totalConIVA,
        referencia: transactionId || `FACT-${Date.now()}`,
        plazo: null,
        periodo: null,
      },
    ];

    return {
      totalNoSuj: 0,
      totalExenta: 0,
      totalGravada: calculoIVA.totalConIVA,
      subTotalVentas: calculoIVA.totalConIVA,
      descuNoSuj: 0,
      descuExenta: 0,
      descuGravada: 0,
      porcentajeDescuento: 0,
      totalDescu: 0,
      tributos: [],
      subTotal: calculoIVA.totalConIVA,
      ivaRete1: 0,
      reteRenta: 0,
      montoTotalOperacion: calculoIVA.totalConIVA,
      totalNoGravado: 0,
      totalPagar: calculoIVA.totalConIVA,
      totalLetras: numeroALetras(calculoIVA.totalConIVA) + " USD",
      saldoFavor: 0,
      condicionOperacion: 1, // Contado
      pagos: pagos, // OBLIGATORIO: Debe contener al menos un pago
      numPagoElectronico: transactionId || `${Date.now()}`,
      totalIva: calculoIVA.iva,
    };
  }

  /**
   * Crea la extensión con información adicional
   */
  private static crearExtension(
    amortization: Amortization,
    paciente: Paciente,
    _transactionId?: string,
    provider?: string
  ): Extension {
    // Construir nombre completo del paciente
    const nombreCompleto = [
      paciente.nombre1,
      paciente.nombre2,
      paciente.apellido1,
      paciente.apellido2,
    ]
      .filter(Boolean)
      .join(" ");

    // Obtener documento del paciente
    const documentoPaciente =
      paciente.dui || paciente.codigoPaciente || "00000000-0";

    // Construir observaciones como en el manual exitoso
    const observaciones = `Pago procesado automáticamente. Provider: ${
      provider?.toUpperCase() || "N/A"
    }, Amount: $${amortization.amount.toFixed(2)}`;

    return {
      nombEntrega: "Clínica Sonríe",
      docuEntrega: this.SONRIE_NIT,
      nombRecibe: nombreCompleto,
      docuRecibe: documentoPaciente,
      observaciones: observaciones,
      placaVehiculo: null,
    };
  }

  /**
   * Crea un preview de la factura sin enviarla
   */
  private static crearFacturaPreview(
    factura: FacturaElectronicaRequest,
    data: AutoFacturacionData
  ): FacturaPreview {
    const { amortization, paciente } = data;

    const nombreCompleto = [
      paciente.nombre1,
      paciente.nombre2,
      paciente.apellido1,
      paciente.apellido2,
    ]
      .filter(Boolean)
      .join(" ");

    const calculoIVA = calcularIVADesdeBase(amortization.amount);

    return {
      factura,
      resumen: {
        cliente: nombreCompleto,
        monto: calculoIVA.baseImponible,
        iva: calculoIVA.iva,
        total: calculoIVA.totalConIVA,
        descripcion:
          factura.cuerpoDocumento[0]?.descripcion || "Servicio odontológico",
      },
    };
  }

  /**
   * Consulta el estado de una factura ya enviada
   */
  public static async consultarFactura(codGeneracion: string): Promise<string> {
    try {
      return await consultarFacturaElectronica({ codGeneracion });
    } catch (error) {
      console.error("Error consultando factura:", error);
      throw error;
    }
  }
}
